import {Injectable, Injector, OnDestroy} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {firstValueFrom, from, Observable, shareReplay} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {Router} from '@angular/router';
import {environment} from '../../environments/environment';
import {VisionConfigService} from './vte/vision-config.service';
import {isDefinitiveAuthRefreshFailure} from './auth-session.util';
import {SessionExpiredService} from './session-expired.service';

/** Refresh this many seconds before access-token exp (proactive, not polling). */
const ACCESS_REFRESH_SKEW_SECONDS = 60;
const AUTH_BROADCAST_CHANNEL = 'kyra-auth';
const REFRESH_LOCK_NAME = 'kyra-token-refresh';
const REFRESH_EPOCH_KEY = 'kyra_refresh_epoch';
const REFRESH_LOCK_KEY = 'kyra_refresh_lock';
const REFRESH_LOCK_TTL_MS = 15000;
const WATCHDOG_RETRY_SECONDS = 60;

type AuthBroadcastMessage =
    | { type: 'session-cleared'; reason?: string; sourceTabId: string }
    | { type: 'tokens-updated'; epoch: number; sourceTabId: string };

/**
 * Central authentication state: JWT storage, silent refresh, cross-tab sync,
 * and session teardown. Navigation + busy cleanup live in SessionExpiredService,
 * which calls {@link AuthService.endSession}.
 */
@Injectable({
    providedIn: 'root',
})
export class AuthService implements OnDestroy {
    private http: HttpClient;
    private tempAccessToken: string | null = null;
    private tempRefreshToken: string | null = null;
    private tempUser: any = null;
    private baseUrl = environment.baseUrl;
    /** Shared in-flight refresh so concurrent 401s / guard checks use one request. */
    private refreshInFlight$: Observable<string> | null = null;
    private accessRefreshTimerId: ReturnType<typeof setTimeout> | null = null;
    private readonly tabId =
        (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
            ? crypto.randomUUID()
            : `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    private authChannel: BroadcastChannel | null = null;
    private storageListener: ((event: StorageEvent) => void) | null = null;
    private visibilityListener: (() => void) | null = null;
    private sessionEndInProgress = false;
    private suppressBroadcast = false;

    constructor(http: HttpClient, private router: Router, private injector: Injector) {
        this.http = http;
        this.installCrossTabCoordination();
    }

    ngOnDestroy(): void {
        this.teardownCrossTabCoordination();
        this.clearAccessRefreshTimer();
    }

    // Called on login
    storeTempLogin(accessToken: string, refToken: string, user: any) {
        this.tempAccessToken = accessToken;
        this.tempRefreshToken = refToken;
        this.tempUser = user;
    }

    saveToLocalStorage() {
        if (this.tempAccessToken && this.tempRefreshToken && this.tempUser) {
            this.writeTokensToStorage({
                access: this.tempAccessToken,
                refresh: this.tempRefreshToken,
                userJson: JSON.stringify(this.tempUser),
                bumpEpoch: true,
                broadcast: true,
            });
        }
        this.clearTemp();
        this.ensureSessionWatchdog();
    }

    updateStoredUser(updates: Record<string, unknown>) {
        const storedUser = localStorage.getItem('user');
        if (!storedUser) {
            return;
        }
        const userObj = JSON.parse(storedUser);
        localStorage.setItem('user', JSON.stringify({...userObj, ...updates}));
    }

    clearTemp() {
        this.tempAccessToken = null;
        this.tempRefreshToken = null;
        this.tempUser = null;
    }

    getToken() {
        return this.tempAccessToken || localStorage.getItem('accessToken');
    }

    getCurrentUserInfo() {
        let user = localStorage.getItem('user') || JSON.stringify(this.tempUser);
        if (user && user !== 'null') {
            const userObj = JSON.parse(user);
            return {
                'id': userObj['id'],
                'role': userObj['groups'],
                'is_superuser': userObj['is_superuser'] === true,
                'is_demo_user': userObj['is_demo_user'] === true,
                'orgId': userObj['org_id'] ?? userObj['orgId'],
                'timezone': userObj['timezone'] || null,
                'subscription_enabled': userObj['subscription_enabled'],
                'visitor_management_enabled': userObj['visitor_management_enabled'],
                'parking_capacity_enabled': userObj['parking_capacity_enabled'],
                'lastName': userObj['last_name'],
                'firstName': userObj['first_name'],
                'camera_esn': userObj['camera_esn'],
                'is_mobile_verified': userObj['is_mobile_verified'] || false,
            };
        }
        return {
            'id': '',
            'role': '',
            'is_superuser': false,
            'is_demo_user': false,
            'orgId': '',
            'timezone': null,
            'subscription_enabled': undefined,
            'visitor_management_enabled': undefined,
            'parking_capacity_enabled': undefined,
            'lastName': '',
            'firstName': '',
            'camera_esn': '',
            'is_mobile_verified': false,
        };
    }

    /** True when the cached user belongs to a customer organization (has org id). */
    hasCustomerOrganization(): boolean {
        const orgId = this.getCurrentUserInfo()?.orgId;
        return orgId !== null && orgId !== undefined && String(orgId).trim() !== '';
    }

    isDemoUser(): boolean {
        return this.getCurrentUserInfo()?.is_demo_user === true;
    }

    /** Organization reporting timezone (IANA). Falls back to Asia/Kolkata. */
    getReportingTimezone(): string {
        const tz = this.getCurrentUserInfo()?.timezone;
        return (typeof tz === 'string' && tz.trim()) ? tz.trim() : 'Asia/Kolkata';
    }

    isSuperUser(): boolean {
        return this.getCurrentUserInfo()?.is_superuser === true;
    }

    isAuthenticated(): boolean {
        return this.hasValidAccessToken();
    }

    getRefreshToken(): string | null {
        return this.tempRefreshToken || localStorage.getItem('refreshToken');
    }

    /**
     * Cross-tab–safe silent refresh.
     * Uses Web Locks (with localStorage lock fallback) so only one tab rotates
     * the refresh token. Other tabs wait, then read the latest credentials from
     * localStorage (source of truth).
     */
    refreshToken(): Observable<string> {
        if (this.refreshInFlight$) {
            return this.refreshInFlight$;
        }

        this.refreshInFlight$ = from(this.runExclusiveRefresh()).pipe(
            finalize(() => {
                this.refreshInFlight$ = null;
            }),
            shareReplay({bufferSize: 1, refCount: true}),
        );

        return this.refreshInFlight$;
    }

    /**
     * Single session teardown path (L2). Clears credentials, temp tokens,
     * watchdog, and optionally broadcasts to other tabs.
     * Does not navigate — callers that need redirect use SessionExpiredService.
     */
    endSession(options?: { reason?: string; broadcast?: boolean }): void {
        const broadcast = options?.broadcast !== false && !this.suppressBroadcast;

        if (this.sessionEndInProgress) {
            this.clearSessionStorageAndTemp();
            return;
        }

        this.sessionEndInProgress = true;
        try {
            this.clearSessionStorageAndTemp();
            if (broadcast) {
                this.postAuthMessage({
                    type: 'session-cleared',
                    reason: options?.reason || 'logout',
                    sourceTabId: this.tabId,
                });
            }
        } finally {
            this.sessionEndInProgress = false;
        }
    }

    /** @deprecated Prefer endSession / SessionExpiredService.forceExpire */
    clearSession() {
        this.endSession({broadcast: false, reason: 'clearSession'});
    }

    /**
     * Explicit user logout and watchdog definitive failures.
     * Delegates navigation + busy cleanup to SessionExpiredService.
     */
    logout(reason: string = 'user-logout'): void {
        // Lazy inject avoids a constructor-level circular DI with SessionExpiredService.
        this.injector.get(SessionExpiredService).forceExpire(reason);
    }

    /**
     * Schedule a single silent refresh shortly before access-token expiry.
     * Safe to call repeatedly; replaces any prior timer. No polling.
     */
    ensureSessionWatchdog(): void {
        this.clearAccessRefreshTimer();

        const expiresAt = this.getAccessTokenExpiresAtSeconds();
        if (expiresAt == null) {
            return;
        }

        const nowSeconds = Math.floor(Date.now() / 1000);
        const refreshInSeconds = Math.max(expiresAt - nowSeconds - ACCESS_REFRESH_SKEW_SECONDS, 0);

        this.accessRefreshTimerId = setTimeout(() => {
            this.accessRefreshTimerId = null;
            if (!this.getRefreshToken()) {
                this.logout('watchdog-no-refresh');
                return;
            }
            this.refreshToken().subscribe({
                next: () => {
                    // ensureSessionWatchdog re-armed after successful write
                },
                error: (err) => {
                    if (isDefinitiveAuthRefreshFailure(err)) {
                        this.logout('watchdog-refresh-invalid');
                    } else {
                        // Network / 5xx: keep credentials; retry later.
                        this.scheduleWatchdogRetry(WATCHDOG_RETRY_SECONDS);
                    }
                },
            });
        }, refreshInSeconds * 1000);
    }

    hasValidAccessToken(): boolean {
        const token = this.getToken();
        if (!token) {
            return false;
        }

        try {
            const payload = JSON.parse(atob(token.split('.')[1] || ''));
            const expiresAt = Number(payload?.exp);
            if (!Number.isFinite(expiresAt)) {
                return false;
            }
            return expiresAt > Math.floor(Date.now() / 1000);
        } catch {
            return false;
        }
    }

    // --- Cross-tab coordination ------------------------------------------------

    private installCrossTabCoordination(): void {
        if (typeof window === 'undefined') {
            return;
        }

        if (typeof BroadcastChannel !== 'undefined') {
            this.authChannel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
            this.authChannel.onmessage = (event: MessageEvent<AuthBroadcastMessage>) => {
                this.onAuthBroadcast(event.data);
            };
        }

        this.storageListener = (event: StorageEvent) => this.onStorageEvent(event);
        window.addEventListener('storage', this.storageListener);

        this.visibilityListener = () => {
            if (document.visibilityState === 'visible') {
                this.onTabBecameVisible();
            }
        };
        document.addEventListener('visibilitychange', this.visibilityListener);
    }

    private teardownCrossTabCoordination(): void {
        if (this.authChannel) {
            this.authChannel.close();
            this.authChannel = null;
        }
        if (typeof window !== 'undefined' && this.storageListener) {
            window.removeEventListener('storage', this.storageListener);
            this.storageListener = null;
        }
        if (typeof document !== 'undefined' && this.visibilityListener) {
            document.removeEventListener('visibilitychange', this.visibilityListener);
            this.visibilityListener = null;
        }
    }

    private onAuthBroadcast(message: AuthBroadcastMessage | null | undefined): void {
        if (!message || message.sourceTabId === this.tabId) {
            return;
        }
        if (message.type === 'session-cleared') {
            this.applyRemoteSessionCleared(message.reason);
            return;
        }
        if (message.type === 'tokens-updated') {
            this.applyRemoteTokensUpdated(message.epoch);
        }
    }

    private onStorageEvent(event: StorageEvent): void {
        if (event.storageArea !== localStorage) {
            return;
        }
        if (event.key === 'accessToken' || event.key === 'refreshToken' || event.key === 'user') {
            if (event.newValue == null) {
                this.applyRemoteSessionCleared('storage-cleared');
            } else if (event.key === 'accessToken' || event.key === 'refreshToken') {
                // Another tab wrote credentials — drop stale in-memory copies.
                this.clearTemp();
                this.ensureSessionWatchdog();
            }
        }
        if (event.key === REFRESH_EPOCH_KEY && event.newValue) {
            this.clearTemp();
            this.ensureSessionWatchdog();
        }
    }

    private onTabBecameVisible(): void {
        // Suspended tabs may hold stale temp tokens after another tab logged out.
        if (!localStorage.getItem('refreshToken') && !localStorage.getItem('accessToken')) {
            if (this.tempAccessToken || this.tempRefreshToken) {
                this.applyRemoteSessionCleared('resume-after-logout');
            }
            return;
        }
        // Prefer storage as source of truth after resume.
        this.clearTemp();
        if (this.hasValidAccessToken()) {
            this.ensureSessionWatchdog();
        }
    }

    private applyRemoteSessionCleared(reason?: string): void {
        this.suppressBroadcast = true;
        try {
            this.clearSessionStorageAndTemp();
        } finally {
            this.suppressBroadcast = false;
        }

        if (this.router.url === '/login') {
            return;
        }

        this.injector.get(SessionExpiredService).forceExpire(reason || 'remote-logout', {
            broadcast: false,
        });
    }

    private applyRemoteTokensUpdated(epoch: number): void {
        const localEpoch = this.readRefreshEpoch();
        if (epoch < localEpoch) {
            // Ignore stale notifications.
            return;
        }
        this.clearTemp();
        this.ensureSessionWatchdog();
    }

    private postAuthMessage(message: AuthBroadcastMessage): void {
        try {
            this.authChannel?.postMessage(message);
        } catch {
            // BroadcastChannel may be unavailable or closed.
        }
    }

    // --- Refresh lock + HTTP ---------------------------------------------------

    private async runExclusiveRefresh(): Promise<string> {
        const locks = typeof navigator !== 'undefined' ? (navigator as any).locks : null;
        if (locks && typeof locks.request === 'function') {
            return locks.request(REFRESH_LOCK_NAME, () => this.performRefreshUnderLock());
        }
        return this.withLocalStorageLock(() => this.performRefreshUnderLock());
    }

    private async performRefreshUnderLock(): Promise<string> {
        // Drop in-memory temps so we always read the latest shared credentials.
        this.tempAccessToken = null;
        this.tempRefreshToken = null;

        const epochBefore = this.readRefreshEpoch();
        const accessFromStorage = localStorage.getItem('accessToken');
        if (accessFromStorage && this.isTokenCurrentlyValid(accessFromStorage)) {
            this.ensureSessionWatchdog();
            return accessFromStorage;
        }

        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        let response: any;
        try {
            response = await firstValueFrom(
                this.http.post<any>(`${this.baseUrl}token/refresh/`, {refresh: refreshToken}),
            );
        } catch (err) {
            throw err;
        }

        const epochAfter = this.readRefreshEpoch();
        if (epochAfter > epochBefore) {
            // Another tab already wrote newer credentials while we were in flight.
            const newerAccess = localStorage.getItem('accessToken');
            if (newerAccess) {
                this.clearTemp();
                this.ensureSessionWatchdog();
                return newerAccess;
            }
        }

        if (!response?.access) {
            throw new HttpErrorResponse({
                status: 401,
                statusText: 'Unauthorized',
                error: {code: 'token_not_valid', detail: 'Token refresh returned no access token'},
            });
        }

        this.writeTokensToStorage({
            access: response.access,
            refresh: response.refresh || refreshToken,
            bumpEpoch: true,
            broadcast: true,
        });
        this.ensureSessionWatchdog();
        return response.access as string;
    }

    private async withLocalStorageLock<T>(fn: () => Promise<T>): Promise<T> {
        const started = Date.now();
        while (Date.now() - started < REFRESH_LOCK_TTL_MS) {
            if (this.tryAcquireLocalRefreshLock()) {
                try {
                    return await fn();
                } finally {
                    this.releaseLocalRefreshLock();
                }
            }
            // Another tab holds the lock — wait briefly, then prefer storage.
            await new Promise((resolve) => setTimeout(resolve, 50));
            const access = localStorage.getItem('accessToken');
            if (access && this.isTokenCurrentlyValid(access)) {
                this.clearTemp();
                this.ensureSessionWatchdog();
                return access as unknown as T;
            }
        }
        // Stale lock must not permanently block refresh — force acquire.
        this.forceAcquireLocalRefreshLock();
        try {
            return await fn();
        } finally {
            this.releaseLocalRefreshLock();
        }
    }

    private tryAcquireLocalRefreshLock(): boolean {
        const now = Date.now();
        const raw = localStorage.getItem(REFRESH_LOCK_KEY);
        if (raw) {
            try {
                const parsed = JSON.parse(raw) as { owner?: string; expiresAt?: number };
                if (parsed.expiresAt && parsed.expiresAt > now && parsed.owner !== this.tabId) {
                    return false;
                }
            } catch {
                // Corrupt lock — overwrite.
            }
        }
        localStorage.setItem(
            REFRESH_LOCK_KEY,
            JSON.stringify({owner: this.tabId, expiresAt: now + REFRESH_LOCK_TTL_MS}),
        );
        // Confirm we won (best-effort under storage contention).
        try {
            const confirmed = JSON.parse(localStorage.getItem(REFRESH_LOCK_KEY) || '{}');
            return confirmed.owner === this.tabId;
        } catch {
            return false;
        }
    }

    private forceAcquireLocalRefreshLock(): void {
        localStorage.setItem(
            REFRESH_LOCK_KEY,
            JSON.stringify({owner: this.tabId, expiresAt: Date.now() + REFRESH_LOCK_TTL_MS}),
        );
    }

    private releaseLocalRefreshLock(): void {
        try {
            const raw = localStorage.getItem(REFRESH_LOCK_KEY);
            if (!raw) {
                return;
            }
            const parsed = JSON.parse(raw);
            if (parsed.owner === this.tabId) {
                localStorage.removeItem(REFRESH_LOCK_KEY);
            }
        } catch {
            localStorage.removeItem(REFRESH_LOCK_KEY);
        }
    }

    private writeTokensToStorage(args: {
        access: string;
        refresh: string;
        userJson?: string;
        bumpEpoch: boolean;
        broadcast: boolean;
    }): void {
        localStorage.setItem('accessToken', args.access);
        localStorage.setItem('refreshToken', args.refresh);
        if (args.userJson) {
            localStorage.setItem('user', args.userJson);
        }
        let epoch = this.readRefreshEpoch();
        if (args.bumpEpoch) {
            epoch += 1;
            localStorage.setItem(REFRESH_EPOCH_KEY, String(epoch));
        }
        if (args.broadcast) {
            this.postAuthMessage({
                type: 'tokens-updated',
                epoch,
                sourceTabId: this.tabId,
            });
        }
    }

    private readRefreshEpoch(): number {
        const raw = localStorage.getItem(REFRESH_EPOCH_KEY);
        const value = Number(raw);
        return Number.isFinite(value) ? value : 0;
    }

    private clearSessionStorageAndTemp(): void {
        this.clearAccessRefreshTimer();
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('activeLink');
        localStorage.removeItem(REFRESH_EPOCH_KEY);
        localStorage.removeItem(REFRESH_LOCK_KEY);
        this.clearTemp();
        this.clearVisionConfig();
    }

    private clearAccessRefreshTimer(): void {
        if (this.accessRefreshTimerId != null) {
            clearTimeout(this.accessRefreshTimerId);
            this.accessRefreshTimerId = null;
        }
    }

    private scheduleWatchdogRetry(afterSeconds: number): void {
        this.clearAccessRefreshTimer();
        this.accessRefreshTimerId = setTimeout(() => {
            this.accessRefreshTimerId = null;
            this.ensureSessionWatchdog();
        }, Math.max(afterSeconds, 5) * 1000);
    }

    private getAccessTokenExpiresAtSeconds(): number | null {
        const token = this.getToken();
        if (!token) {
            return null;
        }
        return this.readTokenExp(token);
    }

    private isTokenCurrentlyValid(token: string): boolean {
        const expiresAt = this.readTokenExp(token);
        if (expiresAt == null) {
            return false;
        }
        return expiresAt > Math.floor(Date.now() / 1000);
    }

    private readTokenExp(token: string): number | null {
        try {
            const payload = JSON.parse(atob(token.split('.')[1] || ''));
            const expiresAt = Number(payload?.exp);
            return Number.isFinite(expiresAt) ? expiresAt : null;
        } catch {
            return null;
        }
    }

    private clearVisionConfig(): void {
        try {
            this.injector.get(VisionConfigService).clear();
        } catch {
            // Vision foundation is optional during session teardown.
        }
    }
}
